/**
 * Provider Detail Page.
 *
 * Route: /platform/providers/:provider_id
 *
 * Shows:
 *   - metadata (name, category, coverage, supported checks, version, docs)
 *   - live health + refresh button
 *   - config schema (what env vars flip it to production)
 *   - Test Connection panel with a sample payload
 *   - Sample response after a test
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  ArrowLeft, RefreshCw, Zap, ShieldCheck, ExternalLink, Terminal,
} from "lucide-react";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

const STATUS_TONE = {
  healthy:  { label: "Healthy",  bg: "#D1FAE5", color: "#065F46" },
  degraded: { label: "Degraded", bg: "#FEF3C7", color: "#92400E" },
  offline:  { label: "Offline",  bg: "#FEE2E2", color: "#7F1D1D" },
  unknown:  { label: "Unknown",  bg: "#F1F5F9", color: "#475569" },
};
const MODE_TONE = {
  sandbox:    { label: "Sandbox",    bg: "#FEF3C7", color: "#92400E" },
  production: { label: "Production", bg: "#D1FAE5", color: "#065F46" },
};

const DEFAULT_TEST_PAYLOAD = {
  "verify-now": {
    category: "identity",
    payload: { national_id: "8001015009087", full_name: "Naledi Test" },
  },
  mock: {
    category: "identity",
    payload: { national_id: "8001015009087", full_name: "Sandbox Consumer" },
  },
};

export default function ProviderDetailPage() {
  const { provider_id } = useParams();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testPayload, setTestPayload] = useState(
    JSON.stringify(DEFAULT_TEST_PAYLOAD[provider_id] || DEFAULT_TEST_PAYLOAD.mock, null, 2),
  );

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/trust-utility/providers/${provider_id}`);
      setProvider(data);
    } catch {
      toast.error("Provider not found");
    } finally {
      setLoading(false);
    }
  }, [provider_id]);

  useEffect(() => { load(); }, [load]);

  async function refresh() {
    setRefreshing(true);
    try {
      await api.post(`/trust-utility/providers/${provider_id}/health`);
      toast.success("Health refreshed");
      load();
    } finally {
      setRefreshing(false);
    }
  }

  async function runTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const body = JSON.parse(testPayload);
      const { data } = await api.post(`/trust-utility/providers/${provider_id}/test`, body);
      setTestResult(data);
      toast.success(`Test executed · ${data.status}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setTestResult({ error: String(msg) });
      toast.error(`Test failed: ${msg}`);
    } finally {
      setTesting(false);
    }
  }

  if (loading) return <div className="p-10 text-sm text-slate-500">Loading provider…</div>;
  if (!provider) return null;

  const tone = STATUS_TONE[provider.health?.status] || STATUS_TONE.unknown;
  const modeTone = MODE_TONE[provider.mode] || MODE_TONE.sandbox;

  return (
    <div className="max-w-4xl mx-auto p-6 lg:p-10 space-y-6" data-testid="provider-detail">
      <Link to="/platform/providers" data-testid="back-to-marketplace" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="w-4 h-4" /> Provider Marketplace
      </Link>

      {/* Header */}
      <header className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>
              {provider.category} · v{provider.version}
            </div>
            <h1 className="font-display text-2xl font-semibold" style={{ color: NAVY }}>{provider.name}</h1>
            <p className="text-[13px] text-slate-600 leading-relaxed mt-2 max-w-3xl">{provider.description}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest font-mono px-2 py-1 rounded" style={{ background: modeTone.bg, color: modeTone.color }}>
              {modeTone.label}
            </span>
            <span data-testid="provider-status" className="text-[10px] uppercase tracking-widest font-mono px-2 py-1 rounded" style={{ background: tone.bg, color: tone.color }}>
              {tone.label}
            </span>
          </div>
        </div>

        {/* Meta */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 pt-3 border-t border-slate-100">
          <Meta label="Countries" value={provider.country_coverage?.join(", ") || "—"} />
          <Meta label="Latency" value={provider.health?.latency_ms != null ? `${provider.health.latency_ms} ms` : "—"} />
          <Meta label="Version" value={provider.health?.version || provider.version} />
          <Meta label="Checks" value={provider.supported_checks?.length || 0} />
        </div>

        <div className="flex gap-2 pt-3 border-t border-slate-100">
          <button
            data-testid="refresh-provider-health"
            onClick={refresh}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-[12px] font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh health
          </button>
          {provider.documentation_url && (
            <a href={provider.documentation_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-[12px] font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50">
              Documentation <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </header>

      {/* Supported checks */}
      <section className="rounded-2xl bg-white border border-slate-100 p-5" data-testid="provider-checks">
        <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">Supported checks</div>
        <div className="flex flex-wrap gap-1">
          {provider.supported_checks.map((c) => (
            <span key={c} className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded" style={{ background: "#F1F5F9", color: "#334155" }}>
              {c.replace(/_/g, " ")}
            </span>
          ))}
        </div>
      </section>

      {/* Config schema */}
      <section className="rounded-2xl bg-white border border-slate-100 p-5" data-testid="provider-config">
        <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">Configuration</div>
        <ul className="text-[13px] text-slate-700 space-y-1">
          <li><strong>Auth method:</strong> {provider.config_schema?.auth_method}</li>
          <li><strong>Timeout:</strong> {provider.config_schema?.timeout_seconds}s</li>
          <li><strong>Retries:</strong> {provider.config_schema?.retry_max}</li>
          {(provider.config_schema?.requires_env || []).length > 0 && (
            <li>
              <strong>Env vars for production:</strong>
              <div className="flex flex-wrap gap-1 mt-1">
                {provider.config_schema.requires_env.map((v) => (
                  <span key={v} className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded"
                    style={{ background: "#F1F5F9", color: "#0F766E" }}>{v}</span>
                ))}
              </div>
            </li>
          )}
        </ul>
      </section>

      {/* Test Connection */}
      <section className="rounded-2xl bg-white border border-slate-100 p-5 space-y-3" data-testid="provider-test-panel">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Test Connection</div>
          <button
            data-testid="run-test-btn"
            onClick={runTest}
            disabled={testing}
            className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-[12px] font-semibold text-white disabled:opacity-50"
            style={{ background: NAVY }}
          >
            <Zap className="w-4 h-4" /> {testing ? "Running…" : "Run test"}
          </button>
        </div>
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Payload</span>
          <textarea
            data-testid="test-payload"
            value={testPayload}
            onChange={(e) => setTestPayload(e.target.value)}
            rows={6}
            className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2 text-[12px] font-mono"
          />
        </label>
        {testResult && (
          <div data-testid="test-result" className="space-y-2">
            <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500">Normalised response</div>
            <pre className="text-[11px] font-mono bg-slate-50 border border-slate-200 rounded-xl p-3 overflow-auto max-h-72">
              {JSON.stringify(testResult, null, 2)}
            </pre>
          </div>
        )}
      </section>

      {/* Positioning footer */}
      <section className="rounded-2xl border-2 border-dashed border-slate-200 p-5 flex items-start gap-3" data-testid="provider-positioning">
        <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5" style={{ color: NAVY }} />
        <div className="text-[12px] text-slate-600 leading-relaxed">
          <strong style={{ color: NAVY }}>Adapter contract.</strong> Nothing above the Trust Utility calls this provider directly. Every workflow, Trust Request and consumer wallet interaction receives the same <em>normalised</em> Trust Response, hash-chained into evidence, regardless of which vendor executed the call.
        </div>
      </section>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">{label}</div>
      <div className="text-[13px] font-semibold" style={{ color: NAVY }}>{value}</div>
    </div>
  );
}
